import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { exportExcel, exportPDF, downloadJSON } from "@/lib/exporters";
import { Button } from "@/components/ui/button";
import { FileJson } from "lucide-react";
import { devError } from "@/lib/logger";

function Row({ label, txval, camt, samt, iamt, csamt, highlight }) {
  return (
    <tr className={highlight ? "bg-blue-50/40 font-semibold" : ""}>
      <td className="text-xs">{label}</td>
      <td className="tx-num">{formatINR(txval ?? 0)}</td>
      <td className="tx-num">{formatINR(iamt ?? 0)}</td>
      <td className="tx-num">{formatINR(camt ?? 0)}</td>
      <td className="tx-num">{formatINR(samt ?? 0)}</td>
      <td className="tx-num">{formatINR(csamt ?? 0)}</td>
    </tr>
  );
}

function SectionTable({ title, hint, children }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-900 text-sm" data-testid={`section-${title.split(".")[0].trim()}`}>{title}</h3>
          {hint && <div className="text-[11px] text-gray-500">{hint}</div>}
        </div>
      </div>
      <div className="border border-gray-200 rounded bg-white overflow-hidden">
        <table className="tx-table">
          <thead>
            <tr>
              <th>Nature of supplies</th>
              <th className="text-right">Total Taxable</th>
              <th className="text-right">IGST</th>
              <th className="text-right">CGST</th>
              <th className="text-right">SGST</th>
              <th className="text-right">Cess</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-gray-200 rounded bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-mono text-base font-semibold ${accent || "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/reports/gstr3b`, { params })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => devError("GSTR-3B report failed", err));
    return () => { mounted = false; };
  }, [activeId, applied.start, applied.end, params]);

  const onJSON = () => {
    if (!data) return;
    downloadJSON(`gstr3b-${active?.name || "report"}`, {
      gstin: data.company.gstin,
      ret_period: data.period || (data.start ? data.start.slice(0, 7) : ""),
      sup_details: {
        osup_det: data.section_3_1.outward_taxable,
        osup_zero: data.section_3_1.outward_zero_rated,
        osup_nil_exmp: data.section_3_1.other_outward_nil_rated,
        isup_rev: data.section_3_1.inward_reverse_charge,
        osup_nongst: data.section_3_1.non_gst_outward,
      },
      inter_sup: { unreg_details: data.section_3_2.rows },
      itc_elg: data.section_4,
      pmt_tx: data.section_6_1,
      _meta: { generated_by: "AXIORA", company: data.company.name, period_start: data.start, period_end: data.end },
    });
  };

  const onExcel = () => {
    if (!data) return;
    const rows = (k) => {
      const r = data.section_3_1[k];
      return [r.label, r.txval, r.iamt, r.camt, r.samt, r.csamt];
    };
    const sheets = [
      {
        name: "3B Summary",
        rows: [
          ["GSTR-3B"],
          ["Company", data.company.name],
          ["GSTIN", data.company.gstin || "—"],
          ["Period", formatRangeLabel(range)],
          [],
          ["3.1 Tax on outward and reverse charge inward supplies"],
          ["Nature of supplies", "Total Taxable", "IGST", "CGST", "SGST", "Cess"],
          rows("outward_taxable"),
          rows("outward_zero_rated"),
          rows("other_outward_nil_rated"),
          rows("inward_reverse_charge"),
          rows("non_gst_outward"),
          [],
          ["3.2 Inter-state supplies to unregistered persons"],
          ["POS (State Code)", "Taxable Value", "IGST"],
          ...data.section_3_2.rows.map((r) => [r.pos, r.txval, r.iamt]),
          [],
          ["4. Eligible ITC"],
          ["Section", "IGST", "CGST", "SGST", "Cess"],
          [data.section_4.itc_available.label, data.section_4.itc_available.iamt, data.section_4.itc_available.camt, data.section_4.itc_available.samt, data.section_4.itc_available.csamt],
          [data.section_4.itc_reversed.label, data.section_4.itc_reversed.iamt, data.section_4.itc_reversed.camt, data.section_4.itc_reversed.samt, data.section_4.itc_reversed.csamt],
          [data.section_4.net_itc_available.label, data.section_4.net_itc_available.iamt, data.section_4.net_itc_available.camt, data.section_4.net_itc_available.samt, data.section_4.net_itc_available.csamt],
          [data.section_4.ineligible_itc.label, data.section_4.ineligible_itc.iamt, data.section_4.ineligible_itc.camt, data.section_4.ineligible_itc.samt, data.section_4.ineligible_itc.csamt],
          [],
          ["6.1 Payment of Tax"],
          ["Description", "IGST", "CGST", "SGST", "Cess"],
          ["Tax payable", data.section_6_1.tax_payable.iamt, data.section_6_1.tax_payable.camt, data.section_6_1.tax_payable.samt, data.section_6_1.tax_payable.csamt],
          ["ITC used", data.section_6_1.itc_credit_used.iamt, data.section_6_1.itc_credit_used.camt, data.section_6_1.itc_credit_used.samt, data.section_6_1.itc_credit_used.csamt],
          ["Tax paid in cash", data.section_6_1.tax_paid_in_cash.iamt, data.section_6_1.tax_paid_in_cash.camt, data.section_6_1.tax_paid_in_cash.samt, data.section_6_1.tax_paid_in_cash.csamt],
          [],
          ["Net cash payable", data.section_6_1.net_cash_payable],
        ],
      },
    ];
    exportExcel(`gstr3b-${active?.name || "report"}`, sheets);
  };

  const onPDF = () => {
    if (!data) return;
    const r31 = ["outward_taxable", "outward_zero_rated", "other_outward_nil_rated", "inward_reverse_charge", "non_gst_outward"]
      .map((k) => {
        const x = data.section_3_1[k];
        return [x.label, formatINR(x.txval), formatINR(x.iamt), formatINR(x.camt), formatINR(x.samt), formatINR(x.csamt)];
      });
    const sec4 = ["itc_available", "itc_reversed", "net_itc_available", "ineligible_itc"].map((k) => {
      const x = data.section_4[k];
      return [x.label, formatINR(x.iamt), formatINR(x.camt), formatINR(x.samt), formatINR(x.csamt)];
    });
    const sections = [
      { title: "3.1 Outward & RCM Inward Supplies", headers: ["Nature", "Taxable", "IGST", "CGST", "SGST", "Cess"], rows: r31 },
      { title: "4. Eligible ITC", headers: ["Section", "IGST", "CGST", "SGST", "Cess"], rows: sec4 },
      { title: "6.1 Payment of Tax", headers: ["Description", "IGST", "CGST", "SGST", "Cess"], rows: [
        ["Tax payable", formatINR(data.section_6_1.tax_payable.iamt), formatINR(data.section_6_1.tax_payable.camt), formatINR(data.section_6_1.tax_payable.samt), formatINR(data.section_6_1.tax_payable.csamt)],
        ["ITC used", formatINR(data.section_6_1.itc_credit_used.iamt), formatINR(data.section_6_1.itc_credit_used.camt), formatINR(data.section_6_1.itc_credit_used.samt), formatINR(data.section_6_1.itc_credit_used.csamt)],
        ["Tax paid in cash", formatINR(data.section_6_1.tax_paid_in_cash.iamt), formatINR(data.section_6_1.tax_paid_in_cash.camt), formatINR(data.section_6_1.tax_paid_in_cash.samt), formatINR(data.section_6_1.tax_paid_in_cash.csamt)],
        ["NET CASH PAYABLE (₹)", formatINR(data.section_6_1.net_cash_payable), "", "", ""],
      ] },
    ];
    if (data.section_3_2.rows.length) {
      sections.splice(1, 0, {
        title: "3.2 Inter-state supplies to unregistered persons",
        headers: ["POS", "Taxable", "IGST"],
        rows: data.section_3_2.rows.map((r) => [r.pos, formatINR(r.txval), formatINR(r.iamt)]),
      });
    }
    exportPDF(`gstr3b-${active?.name || "report"}`, `GSTR-3B — ${formatRangeLabel(range)}`, sections, { company: active?.name });
  };

  return (
    <>
      <PageHeader
        title="GSTR-3B — Summary Return"
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onJSON} disabled={!data} className="h-8" data-testid="btn-export-json">
              <FileJson className="w-4 h-4 mr-1.5" /> JSON
            </Button>
            <ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />
          </div>
        }
      />
      <div className="p-6 space-y-6" data-testid="gstr3b-page">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        {!data && <div className="text-sm text-gray-500" data-testid="gstr3b-loading">Loading…</div>}
        {data && (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="gstr3b-totals">
              <Stat label="Outward Taxable" value={`₹ ${formatINR(data.totals.outward_taxable)}`} />
              <Stat label="Output Tax" value={`₹ ${formatINR(data.totals.outward_tax)}`} accent="text-orange-700" />
              <Stat label="ITC Available" value={`₹ ${formatINR(data.totals.itc_available)}`} accent="text-emerald-700" />
              <Stat label="Net Cash Payable" value={`₹ ${formatINR(data.totals.net_cash_payable)}`} accent="text-red-700" />
            </div>

            {/* 3.1 */}
            <SectionTable title="3.1  Tax on outward and reverse charge inward supplies" hint="Outward taxable supplies (after sales returns) and inward RCM">
              <Row label={data.section_3_1.outward_taxable.label} {...data.section_3_1.outward_taxable} />
              <Row label={data.section_3_1.outward_zero_rated.label} {...data.section_3_1.outward_zero_rated} />
              <Row label={data.section_3_1.other_outward_nil_rated.label} {...data.section_3_1.other_outward_nil_rated} />
              <Row label={data.section_3_1.inward_reverse_charge.label} {...data.section_3_1.inward_reverse_charge} />
              <Row label={data.section_3_1.non_gst_outward.label} {...data.section_3_1.non_gst_outward} />
            </SectionTable>

            {/* 3.2 */}
            {data.section_3_2.rows.length > 0 && (
              <section>
                <h3 className="font-semibold text-gray-900 text-sm mb-2">3.2  Inter-state supplies to unregistered persons</h3>
                <div className="border border-gray-200 rounded bg-white overflow-hidden">
                  <table className="tx-table">
                    <thead><tr><th>POS (State Code)</th><th className="text-right">Taxable</th><th className="text-right">IGST</th></tr></thead>
                    <tbody data-testid="gstr3b-3-2-rows">
                      {data.section_3_2.rows.map((r) => (
                        <tr key={r.pos}>
                          <td className="font-mono text-xs">{r.pos}</td>
                          <td className="tx-num">{formatINR(r.txval)}</td>
                          <td className="tx-num">{formatINR(r.iamt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* 4 */}
            <section>
              <h3 className="font-semibold text-gray-900 text-sm mb-2">4.  Eligible ITC</h3>
              <div className="border border-gray-200 rounded bg-white overflow-hidden">
                <table className="tx-table">
                  <thead>
                    <tr><th>Section</th><th className="text-right">IGST</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">Cess</th></tr>
                  </thead>
                  <tbody data-testid="gstr3b-itc-rows">
                    {["itc_available", "itc_reversed", "net_itc_available", "ineligible_itc"].map((k) => {
                      const r = data.section_4[k];
                      return (
                        <tr key={k} className={k === "net_itc_available" ? "bg-emerald-50/40 font-semibold" : ""}>
                          <td className="text-xs">{r.label}</td>
                          <td className="tx-num">{formatINR(r.iamt)}</td>
                          <td className="tx-num">{formatINR(r.camt)}</td>
                          <td className="tx-num">{formatINR(r.samt)}</td>
                          <td className="tx-num">{formatINR(r.csamt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 6.1 */}
            <section>
              <h3 className="font-semibold text-gray-900 text-sm mb-2">6.1  Payment of Tax</h3>
              <div className="border border-gray-200 rounded bg-white overflow-hidden">
                <table className="tx-table">
                  <thead>
                    <tr><th>Description</th><th className="text-right">IGST</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">Cess</th></tr>
                  </thead>
                  <tbody data-testid="gstr3b-pmt-rows">
                    {["tax_payable", "itc_credit_used", "tax_paid_in_cash"].map((k) => {
                      const r = data.section_6_1[k];
                      const label = k === "tax_payable" ? "Tax payable" : k === "itc_credit_used" ? "ITC used to offset" : "Tax paid in cash";
                      return (
                        <tr key={k}>
                          <td className="text-xs">{label}</td>
                          <td className="tx-num">{formatINR(r.iamt)}</td>
                          <td className="tx-num">{formatINR(r.camt)}</td>
                          <td className="tx-num">{formatINR(r.samt)}</td>
                          <td className="tx-num">{formatINR(r.csamt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div
                className={`mt-3 border rounded p-4 text-right font-mono text-xl font-semibold ${
                  data.section_6_1.net_cash_payable > 0
                    ? "border-red-200 bg-red-50 text-red-800"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800"
                }`}
                data-testid="gstr3b-net-cash"
              >
                Net cash payable: ₹ {formatINR(data.section_6_1.net_cash_payable)}
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}

export default function GSTR3B() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
